import { useMemo } from 'react';
import { makeStyles, Container, Typography, Button, MenuItem } from '@material-ui/core';
import { isArray } from 'lodash';

import { DefaultPointsProps } from './PointsInputs';
import { BaseSelect } from '../form/BaseSelect';

import clearIcon from '../../../assets/icons/clear-icon.svg';

import { darkGreen } from 'src/data/colors';

export interface SelectMenuProps extends DefaultPointsProps {
  options: string[];
  multiple?: boolean;
}

interface FriendlyMeaning {
  value: string;
  name: string;
}

const useStyles = makeStyles(() => ({
  clear: {
    color: darkGreen,
    display: 'flex',
    fontSize: 12,
    fontWeight: 500,
    justifyContent: 'flex-end',
    width: '100%',
  },
  clearIcon: {
    padding: 0,
    minWidth: 'unset',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0',
  },
  selectedFieldsContainer: {
    alignItems: 'center',
    border: '1px solid rgba(78, 100, 111, 0.10)',
    borderRadius: 4,
    display: 'flex',
    height: 48,
    padding: '8px 16px',
    margin: '8px 0 0',
    justifyContent: 'space-between',
  },
  label: {
    marginBottom: 4,
    fontSize: 12,
    fontWeight: 500,
  },
  icon: {
    position: 'absolute',
    right: 22,
    pointerEvents: 'none',
  },
  menuContainer: {
    borderRadius: 8,
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 0',
  },
  fieldsTitle: {
    fontWeight: 500,
  },
}));

const SelectMenu = ({ value, onChange, multiple = false, point }: SelectMenuProps) => {
  const classes = useStyles();

  const {
    entry: { friendlyMeanings, meanings },
    readOnly,
  } = point;

  const transformFriendlyMeanings = (obj: { [key: string]: string }): FriendlyMeaning[] => {
    return Object.entries(obj).map(([key, value]) => ({
      value: key,
      name: value,
    }));
  };

  const meaningsToUse =
    friendlyMeanings && Object.keys(friendlyMeanings).length > 0 ? friendlyMeanings : meanings;
  const options = useMemo(
    () => (meaningsToUse ? transformFriendlyMeanings(meaningsToUse) : []),
    [meaningsToUse]
  );

  const handleChange = (newValue: string) => {
    if (readOnly) return;

    if (multiple) {
      const newValueArray = value as string[];

      if (!newValueArray.includes(newValue)) {
        onChange(point, [...newValueArray, newValue]);
      }
      return;
    }

    onChange(point, newValue);
  };

  return (
    <Container className={classes.inputContainer}>
      <BaseSelect
        onChange={(e) => {
          return handleChange(e.target.value as string);
        }}
        value={String(value)}
        inputProps={{ size: 'small' }}
      >
        {options.map((option) => (
          <MenuItem key={`${option.name}-${option.value}`} value={option.value}>
            {option.name}
          </MenuItem>
        ))}
      </BaseSelect>

      {multiple && (
        <>
          <Container className={classes.fieldsContainer}>
            <Typography className={classes.fieldsTitle}>Selected Fields</Typography>
            {isArray(value) && value.length ? (
              value.map((val) => (
                <Container key={val} className={classes.selectedFieldsContainer}>
                  <Typography>{val}</Typography>
                  <Button
                    className={classes.clearIcon}
                    disableRipple
                    onClick={() =>
                      onChange(
                        point,
                        value.filter((v) => v !== val)
                      )
                    }
                  >
                    <img src={clearIcon} alt="clear" />
                  </Button>
                </Container>
              ))
            ) : (
              <Typography>Your selected fields will appear here</Typography>
            )}
          </Container>
          <Button className={classes.clear} onClick={() => onChange(point, [])}>
            Clear all sections
          </Button>
        </>
      )}
    </Container>
  );
};

export default SelectMenu;
